import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, ArrowRight, Clock, ShieldCheck, Activity, HelpCircle, Zap, Heart } from 'lucide-react';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.6,
  delay,
  ease: 'easeOut'
}} data-id="element-0">
    {children}
  </motion.div>;
const CTAButton = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <a href="#offer" onClick={e => {
  e.preventDefault();
  document.getElementById('offer')?.scrollIntoView({
    behavior: 'smooth'
  });
}} className={`bg-brand-teal hover:bg-[#4A8D9C] text-white rounded-full px-8 py-4 font-semibold text-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 mx-auto cursor-pointer ${className}`} data-id="element-1">
    {children}
    <ArrowRight className="w-5 h-5" data-id="element-2" />
  </a>;
export function App() {
  return <div className="min-h-screen selection:bg-brand-lavender selection:text-brand-purple" data-id="element-3">
      {/* 1. HERO SECTION */}
      <section className="pt-20 pb-16 px-6 md:px-12 max-w-5xl mx-auto text-center" data-id="element-4">
        <FadeIn data-id="element-5">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-brand-dark leading-tight mb-6" data-id="element-6">
            The 7-Day Sciatica Fix: How to Stop the "Lightning Bolt" Leg Pain
            from Your Living Room Floor
          </h1>
          <h2 className="text-xl md:text-2xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed font-sans font-light" data-id="element-7">
            A 10-Minute Daily "Neural Space" Protocol for Women 35+ to Eliminate
            Radiating Nerve Pain Without Surgery, Expensive Co-pays, or
            Aggressive Stretching.
          </h2>

          <div className="relative rounded-2xl overflow-hidden shadow-2xl mb-12 max-w-4xl mx-auto border-4 border-white" data-id="element-8">
            <img src="https://cdn.magicpatterns.com/uploads/nid5FJroahqLZnniMw7fwm/7_day_sciatica.png" alt="7-Day Sciatica Relief" className="w-full h-auto object-cover" data-id="element-9" />
          </div>

          <CTAButton data-id="element-10">Get The 7-Day Sciatica Fix Now</CTAButton>
          <p className="mt-4 text-sm text-gray-500 font-medium" data-id="element-11">
            *Instant Digital Access. Start your first 10-minute reset in the
            next 5 minutes.
          </p>
        </FadeIn>
      </section>

      {/* 2. OPENING HOOK */}
      <section className="py-20 bg-white" data-id="element-12">
        <div className="max-w-3xl mx-auto px-6" data-id="element-13">
          <FadeIn data-id="element-14">
            <p className="text-lg md:text-xl leading-relaxed mb-6 text-gray-700" data-id="element-15">
              <span className="font-bold text-brand-purple text-2xl font-serif block mb-2" data-id="element-16">
                It’s 7:15 AM.
              </span>
              You’re standing in front of your closet, and your biggest fear
              isn’t what to wear—it’s how you’re going to get your socks on
              without that familiar, white-hot "zing" ripping through your right
              leg.
            </p>
            <p className="text-lg md:text-xl leading-relaxed mb-6 text-gray-700" data-id="element-17">
              You’ve tried the heating pads, the Ibuprofen, and the "yoga for
              back pain" videos that only seem to make the throbbing worse by
              the time you go to bed.
            </p>
            <p className="text-lg md:text-xl leading-relaxed font-medium text-brand-dark" data-id="element-18">
              You feel like you’re eighty years old before you’ve even hit
              forty.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 3. THE PROBLEM */}
      <section className="py-20 bg-brand-lavender" data-id="element-19">
        <div className="max-w-3xl mx-auto px-6" data-id="element-20">
          <FadeIn data-id="element-21">
            <h3 className="text-3xl md:text-4xl font-bold mb-8 text-brand-purple" data-id="element-22">
              The reason most "cures" fail is simple:
            </h3>
            <p className="text-lg md:text-xl leading-relaxed mb-6 text-gray-800" data-id="element-23">
              They treat your muscles, but your problem is a nerve. When your
              sciatic nerve is pinched at the L4-S1 vertebrae, traditional
              hamstring stretching is like yanking on a tangled guitar string.
              It doesn't fix the knot; it just makes it tighter.
            </p>
            <p className="text-lg md:text-xl leading-relaxed mb-6 text-gray-800" data-id="element-24">
              If you sit for more than 4 hours a day, your pelvis is likely
              "tucked," physically choking the thickest nerve in your body.
            </p>
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-sm border-l-4 border-brand-teal mt-8" data-id="element-25">
              <p className="text-xl md:text-2xl font-serif italic text-brand-dark" data-id="element-26">
                "You aren't 'broken' or 'old'—you are compressed. And you cannot
                medicate a mechanical pinch away."
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 4. WHY WHAT YOU TRIED DIDN'T WORK */}
      <section className="py-20 bg-white" data-id="element-27">
        <div className="max-w-3xl mx-auto px-6" data-id="element-28">
          <FadeIn data-id="element-29">
            <h3 className="text-3xl md:text-4xl font-bold mb-10 text-brand-dark text-center" data-id="element-30">
              Why What You Tried Didn’t Work
            </h3>
            <ul className="space-y-6" data-id="element-31">
              {['Traditional "forward-fold" stretches actually pull the nerve tighter against the spinal disc.', 'High-dose painkillers mask the chemical signal while the mechanical "pinch" remains active.', 'Foam rolling the lower back can increase spinal pressure and trigger a massive flare-up.', '"Waiting for it to go away" allows inflammatory fluid to pool around the nerve root, causing the "11 PM Throb."'].map((item, i) => <li key={i} className="flex items-start gap-4 p-4 rounded-lg hover:bg-brand-warm transition-colors" data-id="element-32">
                  <XCircle className="text-red-400 w-6 h-6 shrink-0 mt-1" data-id="element-33" />
                  <span className="text-lg text-gray-700 leading-relaxed" data-id="element-34">
                    {item}
                  </span>
                </li>)}
            </ul>
          </FadeIn>
        </div>
      </section>

      {/* 5. THE UNIQUE MECHANISM */}
      <section className="py-24 bg-brand-dark text-white relative overflow-hidden" data-id="element-35">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none" data-id="element-36">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-brand-teal rounded-full blur-3xl" data-id="element-37"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-brand-purple rounded-full blur-3xl" data-id="element-38"></div>
        </div>
        <div className="max-w-3xl mx-auto px-6 relative z-10" data-id="element-39">
          <FadeIn data-id="element-40">
            <div className="flex justify-center mb-6" data-id="element-41">
              <Zap className="w-12 h-12 text-brand-teal" data-id="element-42" />
            </div>
            <h3 className="text-3xl md:text-4xl font-bold mb-8 text-center" data-id="element-43">
              The "Neural Space" Protocol
            </h3>
            <p className="text-lg md:text-xl leading-relaxed mb-6 text-gray-300 text-center" data-id="element-44">
              A sequence of floor-based micro-movements designed to use gravity
              as your traction. Instead of "stretching," we are "decompressing."
            </p>
            <p className="text-lg md:text-xl leading-relaxed text-gray-300 text-center" data-id="element-45">
              By placing the spine in a specific 90/90 neutral reset, we open
              the "exit doors" in your vertebrae, allowing the sciatic nerve to
              glide freely for the first time in years.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 6. WHAT YOU GET */}
      <section className="py-20 bg-brand-warm" data-id="element-46">
        <div className="max-w-4xl mx-auto px-6" data-id="element-47">
          <FadeIn data-id="element-48">
            <h3 className="text-3xl md:text-4xl font-bold mb-12 text-center text-brand-dark" data-id="element-49">
              What You Get Inside The Fix
            </h3>
            <div className="grid md:grid-cols-2 gap-6 mb-12" data-id="element-50">
              {[{
              title: 'The 7-Day Reset Schedule',
              desc: 'A day-by-day roadmap from "Inflamed" to "Mobile."'
            }, {
              title: 'The 90/90 Neutral Reset',
              desc: 'The exact floor position that unloads 100% of your spinal weight.'
            }, {
              title: 'The 3-Point Nerve Glide',
              desc: 'A subtle movement to "un-tether" the nerve from your glute and calf.'
            }, {
              title: 'The Sitting Survival Guide',
              desc: 'How to "bulletproof" your car seat and office chair in 60 seconds.'
            }, {
              title: 'The Morning Shoe-Tying Hack',
              desc: 'The essential safety move to prevent 8 AM flare-ups.'
            }].map((item, i) => <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-start gap-4" data-id="element-51">
                  <CheckCircle2 className="text-brand-teal w-8 h-8 shrink-0" data-id="element-52" />
                  <div data-id="element-53">
                    <h4 className="font-bold text-lg mb-2 text-brand-dark" data-id="element-54">
                      {item.title}
                    </h4>
                    <p className="text-gray-600" data-id="element-55">{item.desc}</p>
                  </div>
                </div>)}
            </div>
            <CTAButton data-id="element-56">Get Instant Access Now</CTAButton>
          </FadeIn>
        </div>
      </section>

      {/* 7. WHO THIS IS FOR / NOT FOR */}
      <section className="py-20 bg-white" data-id="element-57">
        <div className="max-w-5xl mx-auto px-6" data-id="element-58">
          <FadeIn data-id="element-59">
            <div className="grid md:grid-cols-2 gap-8" data-id="element-60">
              {/* For */}
              <div className="bg-brand-lavender p-8 rounded-2xl" data-id="element-61">
                <div className="flex items-center gap-3 mb-6" data-id="element-62">
                  <Heart className="text-brand-purple w-8 h-8" data-id="element-63" />
                  <h4 className="text-2xl font-bold text-brand-dark" data-id="element-64">
                    Who This Is For
                  </h4>
                </div>
                <ul className="space-y-4" data-id="element-65">
                  {['Women 35+ who feel a "sharp spark" when driving or sitting.', 'Office professionals who struggle with a constant dull throb in the glute or hip.', 'Moms who noticed nerve pain "creeping in" post-pregnancy or from carrying toddlers.', 'Anyone who wants to avoid the "surgical path" and reclaim their daily mobility.'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-66">
                      <CheckCircle2 className="text-brand-purple w-5 h-5 shrink-0 mt-1" data-id="element-67" />
                      <span className="text-gray-700" data-id="element-68">{item}</span>
                    </li>)}
                </ul>
              </div>

              {/* Not For */}
              <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100" data-id="element-69">
                <div className="flex items-center gap-3 mb-6" data-id="element-70">
                  <ShieldCheck className="text-gray-400 w-8 h-8" data-id="element-71" />
                  <h4 className="text-2xl font-bold text-brand-dark" data-id="element-72">
                    Who This Is Not For
                  </h4>
                </div>
                <ul className="space-y-4" data-id="element-73">
                  {['People with recent spinal fractures or acute trauma.', 'Those looking for a "vibrating belt" or a "magic pill" solution.', 'Anyone unwilling to commit 10 minutes a day to quiet floor work.'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-74">
                      <XCircle className="text-gray-400 w-5 h-5 shrink-0 mt-1" data-id="element-75" />
                      <span className="text-gray-600" data-id="element-76">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 8. HOW IT WORKS */}
      <section className="py-20 bg-brand-warm" data-id="element-77">
        <div className="max-w-3xl mx-auto px-6" data-id="element-78">
          <FadeIn data-id="element-79">
            <h3 className="text-3xl md:text-4xl font-bold mb-12 text-center text-brand-dark" data-id="element-80">
              How It Works
            </h3>
            <div className="space-y-8 relative before:absolute before:inset-0 before:ml-6 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-brand-teal/30 before:to-transparent" data-id="element-81">
              {[{
              title: 'Lock in the Neutral Spine',
              desc: 'Use the 90/90 reset to stop the active pinch.'
            }, {
              title: 'Clear the Inflammation',
              desc: 'Use the "Belly Breath" sequence to lower cortisol and relax the psoas.'
            }, {
              title: 'Re-introduce Glide',
              desc: 'Use the 3-point nerve glide to restore pain-free movement to the leg.'
            }].map((step, i) => <div key={i} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-82">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-brand-warm bg-brand-teal text-white font-bold text-xl shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-md z-10" data-id="element-83">
                    {i + 1}
                  </div>
                  <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-84">
                    <h4 className="text-xl font-bold mb-2 text-brand-dark" data-id="element-85">
                      {step.title}
                    </h4>
                    <p className="text-gray-600" data-id="element-86">{step.desc}</p>
                  </div>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 9. PROOF / BELIEVABILITY */}
      <section className="py-20 bg-brand-purple text-white" data-id="element-87">
        <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-88">
          <FadeIn data-id="element-89">
            <Activity className="w-12 h-12 mx-auto mb-6 text-brand-lavender opacity-80" data-id="element-90" />
            <h3 className="text-2xl md:text-3xl font-serif font-medium leading-relaxed mb-8" data-id="element-91">
              "Most users report a 50% reduction in the '11 PM Throb' within the
              first 48 hours of implementing the Neutral Spine Reset."
            </h3>
            <p className="text-lg md:text-xl text-brand-lavender opacity-90 leading-relaxed max-w-3xl mx-auto" data-id="element-92">
              By Day 7, the "lightning bolt" sensations during normal daily
              activities like walking or driving are typically replaced by a
              sense of space and lightness in the lower back.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 10. FAQ */}
      <section className="py-20 bg-white" data-id="element-93">
        <div className="max-w-3xl mx-auto px-6" data-id="element-94">
          <FadeIn data-id="element-95">
            <h3 className="text-3xl md:text-4xl font-bold mb-10 text-center text-brand-dark" data-id="element-96">
              Frequently Asked Questions
            </h3>
            <div className="space-y-6" data-id="element-97">
              {[{
              q: 'I’m not flexible, can I do this?',
              a: 'YES. This is about decompression, not flexibility. If you can lie on the floor, you can do this.'
            }, {
              q: 'Will this work for a herniated disc?',
              a: 'This protocol is designed specifically to create space around discs to reduce nerve contact.'
            }, {
              q: 'How soon will I feel a difference?',
              a: 'Most women feel immediate relief in the "sharpness" of the pain after the very first 10-minute reset.'
            }].map((faq, i) => <div key={i} className="bg-brand-warm p-6 md:p-8 rounded-xl" data-id="element-98">
                  <h4 className="font-bold text-lg md:text-xl mb-3 flex items-start gap-3 text-brand-dark" data-id="element-99">
                    <HelpCircle className="text-brand-teal w-6 h-6 shrink-0 mt-0.5" data-id="element-100" />
                    {faq.q}
                  </h4>
                  <p className="text-gray-700 ml-9 leading-relaxed" data-id="element-101">{faq.a}</p>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 11. OFFER + PRICE BLOCK */}
      <section id="offer" className="py-24 bg-brand-lavender text-center border-t border-brand-purple/10" data-id="element-102">
        <div className="max-w-3xl mx-auto px-6" data-id="element-103">
          <FadeIn data-id="element-104">
            <h2 className="text-3xl md:text-5xl font-bold mb-8 text-brand-dark leading-tight" data-id="element-105">
              Reclaim your mobility for less than the cost of a single physical
              therapy co-pay.
            </h2>

            <div className="bg-white p-8 md:p-12 rounded-3xl shadow-xl mb-10 max-w-2xl mx-auto border border-brand-purple/10 relative overflow-hidden" data-id="element-106">
              <div className="absolute top-0 left-0 w-full h-2 bg-brand-teal" data-id="element-107"></div>
              <p className="text-xl md:text-2xl mb-4 text-gray-600 font-medium" data-id="element-108">
                Get THE 7-DAY SCIATICA FIX today for a one-time investment of
              </p>
              <div className="flex items-center justify-center gap-4 mb-8" data-id="element-109">
                <span className="text-3xl md:text-4xl font-bold text-gray-400 line-through font-serif" data-id="element-110">
                  $10
                </span>
                <span className="text-6xl md:text-7xl font-bold text-brand-teal font-serif" data-id="element-111">
                  $8
                </span>
              </div>
              <div className="inline-block bg-brand-teal/10 text-brand-teal font-semibold text-sm px-4 py-1.5 rounded-full mb-6" data-id="element-112">
                Save 20% — Limited Time
              </div>

              <a href="https://online30.com/cart/46653904683166:1" target="_blank" rel="noopener noreferrer" className="bg-brand-teal hover:bg-[#4A8D9C] text-white rounded-full px-8 py-5 font-semibold text-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 mx-auto cursor-pointer w-full md:w-auto md:px-12" data-id="element-113">
                Get The 7-Day Sciatica Fix Now
                <ArrowRight className="w-5 h-5" data-id="element-114" />
              </a>

              <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-500 font-medium" data-id="element-115">
                <Clock className="w-4 h-4" data-id="element-116" />
                <span data-id="element-117">
                  *Instant Digital Access. Start your first 10-minute reset in
                  the next 5 minutes.
                </span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-4 text-gray-400 text-sm" data-id="element-118">
              <span data-id="element-119">Secure Checkout</span>
              <span data-id="element-120">•</span>
              <span data-id="element-121">One-Time Payment</span>
              <span data-id="element-122">•</span>
              <span data-id="element-123">Lifetime Access</span>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <Footer data-id="element-124" />
    </div>;
}
const PRIVACY_CONTENT = `
Last updated: March 28, 2026

Your privacy is important to us. This Privacy Policy explains how The 7-Day Sciatica Fix ("we," "us," or "our") collects, uses, and protects your personal information.

Information We Collect
We collect information you provide directly, such as your name and email address when you purchase our digital product. We also collect payment information through our secure third-party payment processor.

How We Use Your Information
We use your information solely to deliver the purchased digital product, send transactional emails related to your purchase, and improve our services.

Data Protection
We implement industry-standard security measures to protect your personal information. We do not sell, trade, or rent your personal data to third parties.

Cookies
Our website may use cookies to enhance your browsing experience. You can choose to disable cookies through your browser settings.

Third-Party Services
We use third-party payment processors to handle transactions. These services have their own privacy policies governing the use of your information.

Your Rights
You have the right to access, correct, or delete your personal information at any time. You may also opt out of marketing communications.

Changes to This Policy
We reserve the right to update this Privacy Policy at any time. Changes will be posted on this page with an updated revision date.
`.trim();
const TERMS_CONTENT = `
Last updated: March 28, 2026

Please read these Terms of Service ("Terms") carefully before purchasing or using The 7-Day Sciatica Fix ("the Product").

Acceptance of Terms
By purchasing and accessing the Product, you agree to be bound by these Terms. If you do not agree, do not purchase or use the Product.

Product Description
The 7-Day Sciatica Fix is a digital educational product providing information about movement protocols for sciatica relief. It is not a substitute for professional medical advice, diagnosis, or treatment.

Medical Disclaimer
The content provided is for informational and educational purposes only. Always consult with a qualified healthcare professional before beginning any new exercise or movement protocol. If you experience severe pain, numbness, or loss of function, seek immediate medical attention.

Digital Delivery
Upon purchase, you will receive instant digital access to the Product. Due to the digital nature of this product, all sales are final.

Intellectual Property
All content within the Product is protected by copyright. You may not reproduce, distribute, or share the Product without written permission.

Limitation of Liability
The 7-Day Sciatica Fix and its creators are not liable for any injuries, damages, or adverse effects resulting from the use of the information provided. You assume full responsibility for your use of the Product.

Refund Policy
Due to the instant digital delivery nature of this product, all sales are final. If you experience technical issues accessing the product, please reach out for assistance.

Governing Law
These Terms shall be governed by and construed in accordance with applicable laws.

Changes to Terms
We reserve the right to modify these Terms at any time. Continued use of the Product constitutes acceptance of updated Terms.
`.trim();
function LegalModal({
  isOpen,
  onClose,
  title,
  content
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  content: string;
}) {
  return <AnimatePresence data-id="element-125">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose} data-id="element-126">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-127">
            <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-128">
              <h3 className="text-2xl font-bold text-brand-dark" data-id="element-129">{title}</h3>
              <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600 text-2xl leading-none" data-id="element-130">
                ×
              </button>
            </div>
            <div className="p-6 overflow-y-auto" data-id="element-131">
              {content.split('\n\n').map((paragraph, i) => {
            const isHeading = paragraph.length < 60 && !paragraph.includes('.');
            return isHeading ? <h4 key={i} className="font-bold text-brand-dark mt-6 mb-2 text-lg" data-id="element-132">
                    {paragraph}
                  </h4> : <p key={i} className="text-gray-600 leading-relaxed mb-4" data-id="element-133">
                    {paragraph}
                  </p>;
          })}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
function Footer() {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  return <>
      <footer className="py-8 bg-brand-dark text-center" data-id="element-134">
        <div className="max-w-3xl mx-auto px-6 flex flex-col items-center gap-4" data-id="element-135">
          <div className="flex items-center gap-6 text-sm" data-id="element-136">
            <button onClick={() => setShowPrivacy(true)} className="text-gray-400 hover:text-white transition-colors underline underline-offset-2" data-id="element-137">
              Privacy Policy
            </button>
            <span className="text-gray-600" data-id="element-138">•</span>
            <button onClick={() => setShowTerms(true)} className="text-gray-400 hover:text-white transition-colors underline underline-offset-2" data-id="element-139">
              Terms of Service
            </button>
          </div>
          <p className="text-gray-500 text-xs" data-id="element-140">
            © {new Date().getFullYear()} The 7-Day Sciatica Fix. All rights
            reserved.
          </p>
        </div>
      </footer>

      <LegalModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} title="Privacy Policy" content={PRIVACY_CONTENT} data-id="element-141" />
      <LegalModal isOpen={showTerms} onClose={() => setShowTerms(false)} title="Terms of Service" content={TERMS_CONTENT} data-id="element-142" />
    </>;
}